import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;
type GetStateFn = () => RootState;

const getFeatureFlagsData = async (
  getState: GetStateFn,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  const instanceId = getInstanceId(getState());
  try {
    const response = await awsClientGateway({
      pathTemplate: `/featureFlag/${instanceId}?apiName=featureFlags`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `featureFlags`,
      message: `Something went wrong in getFeatureFlags API => ${error}`,
    });
    logMessage(
      `featureFlags`,
      `Something went wrong in getFeatureFlags API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getFeatureFlags api endpoint");
  }
};

export const getFeatureFlags = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>(
  "featureFlag/getFeatureFlags",
  async (_args, { getState, rejectWithValue }) =>
    await getFeatureFlagsData(getState, rejectWithValue),
);

export const createOrUpdateFeatureFlags = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "/content/createOrUpdateFeatureFlags",
  async (featureFlagsAttributes, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    try {
      await awsClientGateway({
        pathTemplate: `/featureFlag/${instanceId}`,
        method: "POST",
        body: { ...featureFlagsAttributes },
      });
      return await getFeatureFlagsData(getState, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `featureFlags`,
        message: `Something went wrong in createOrUpdateFeatureFlags API => ${error}`,
      });
      logMessage(
        `featureFlags`,
        `Something went wrong in createOrUpdateFeatureFlags API => ${error}`,
        `error`,
      );
      return rejectWithValue(
        "Error in createOrUpdateFeatureFlags api endpoint",
      );
    }
  },
);

export const deleteFeatureFlags = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "/content/deleteFeatureFlags",
  async (deleteAttribute, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    try {
      await awsClientGateway({
        pathTemplate: `/featureFlag/${instanceId}/`,
        method: "DELETE",
        body: { ...deleteAttribute },
      });
      return await getFeatureFlagsData(getState, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `featureFlags`,
        message: `Something went wrong in deleteFeatureFlags API => ${error}`,
      });
      logMessage(
        `featureFlags`,
        `Something went wrong in deleteFeatureFlags API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in deleteFeatureFlags api endpoint");
    }
  },
);

export const getTicketOrCaseFeatureFlagEnabled = createAction(
  "ccp/getTicketOrCaseFeatureFlagEnabled",
);
